import { useState } from 'react';
import { useSnackbar } from '@ghosts-inc/design-system/hooks';
import { AnalyticsEventNames } from '@/analytics/Event.types';
import { PageContext, SectionContext } from '@/analytics/analytics.types';
import { analytics } from '@/analytics/segment';
import { logEvent } from '@/legacy/user-events/log-event';
import { UserEventTypes } from '@/legacy/user-events/user-events.types';
import { setFavoriteListing } from './actions';
import { deleteFavoriteListing } from './actions';

type UseFavoriteHeartProps = {
  analyticsInfo?: { context?: PageContext & SectionContext };
  initialFavoriteState?: boolean;
  listingId: number;
  onFavoriteClick?: () => void;
  withSnackbarNotification?: boolean;
};

export const useFavoriteHeart = ({
  analyticsInfo,
  initialFavoriteState,
  listingId,
  onFavoriteClick,
  withSnackbarNotification = false,
}: UseFavoriteHeartProps) => {
  const { showSnackbar } = useSnackbar();
  const [isFavorite, setIsFavorite] = useState(!!initialFavoriteState);

  const toggle = async (event: React.MouseEvent<HTMLElement>) => {
    // prevent the click from propagating to the parent,
    // which could be a clickable component like a ListingCard
    event.stopPropagation();
    event.preventDefault();

    if (isFavorite) {
      await deleteFavoriteListing(listingId);
      analytics.track(AnalyticsEventNames.LISTING_UNFAVORITED, {
        listing_id: listingId,
        ...analyticsInfo,
      });
      if (withSnackbarNotification) {
        showSnackbar({ action: <></>, severity: 'alertSuccess', title: 'Listing Unsaved' });
      }
    } else {
      await setFavoriteListing(listingId);
      analytics.track(AnalyticsEventNames.LISTING_FAVORITED, {
        listing_id: listingId,
        ...analyticsInfo,
      });
      logEvent(UserEventTypes.HEART_CLICK_TO_FAVORITE, listingId);
      if (withSnackbarNotification) {
        showSnackbar({ action: <></>, severity: 'alertSuccess', title: 'Listing Saved!' });
      }
    }
    setIsFavorite((favorite) => !favorite);
    onFavoriteClick?.();
  };

  return { toggle, isFavorite };
};
